import React from 'react';
import { getTableStyles } from './styleUtils';
import { theme } from './theme';
import './Table.css';

type CellRenderer = (value: any, rowData: any) => React.ReactNode;

export type ColumnDefinition = {
	header: string | React.ReactNode;
	key: string;
	cellRenderer?: CellRenderer;
};

export type TableProps = {
	columns: ColumnDefinition[];
	data: any[];
	className?: string;
	maxWidth?: string | number;
	maxHeight?: string | number;
	dataTestId?: string;
};

const TableHeader: React.FC<{ columns: ColumnDefinition[]; styles: any }> = ({
	columns,
	styles
}) => (
	<thead style={styles.tableHeader_css} className="table-header">
		<tr>
			{columns.map((column) => (
				<th key={column.key} style={styles.tableHeaderCell_css} className="table-header-cell">
					{column.header}
				</th>
			))}
		</tr>
	</thead>
);

const TableRow: React.FC<{
	row: any;
	columns: ColumnDefinition[];
	styles: any;
	rowIndex: number;
}> = ({ row, columns, styles, rowIndex }) => {
	return (
		<tr key={`${rowIndex}-${'row'}`} style={styles.tableRow_css} className="table-row">
			{columns.map((column) => (
				<td
					key={`${rowIndex}-${column.key}`}
					className={`table-cell responsive-cell`}
					style={{ ...styles.tableCell_css }}
					data-label={column.header}
				>
					<div className="table-cell-content">
						<span className="table-cell-header">{column.header}</span>
						<span>
							{column.cellRenderer ? column.cellRenderer(row[column.key], row) : row[column.key]}
						</span>
					</div>
				</td>
			))}
		</tr>
	);
};
const TableBody: React.FC<{
	data: any[];
	columns: ColumnDefinition[];
	styles: any;
}> = ({ data, columns, styles }) => (
	<tbody>
		{data.length > 0 ? (
			data.map((row, rowIndex) => (
				<TableRow key={rowIndex} row={row} columns={columns} styles={styles} rowIndex={rowIndex} />
			))
		) : (
			<tr className="table-row">
				<td className={`table-cell`} colSpan={columns.length}>
					<div className="flex items-center justify-center">
						<span>
							<img width={170} height={150} src="/noData.svg" alt="No data available" />
						</span>
					</div>
				</td>
			</tr>
		)}
	</tbody>
);

export const Table: React.FC<TableProps> = ({
	columns,
	data,
	className,
	maxWidth,
	maxHeight,
	dataTestId
}) => {
	const styles = getTableStyles(theme);
	const containerStyle = {
		...styles.tableContainer_css,
		maxWidth: maxWidth,
		overflowX: maxWidth ? ('auto' as const) : ('visible' as const)
	};

	return (
		<div
			style={{
				...containerStyle,
				height: maxHeight,
				overflowY: maxHeight ? 'auto' : 'visible'
			}}
			className={className}
		>
			<table data-testid={dataTestId} style={styles.table_css} className="table">
				<TableHeader columns={columns} styles={{ ...styles, position: 'fixed' }} />
				<TableBody data={data} columns={columns} styles={styles} />
			</table>
		</div>
	);
};
